import { type AnyRouter, useRouter } from '@tanstack/react-router'
import { pagesNotListedAsRecent } from 'features/commandPalette/utils/commandPaletteRecents'
import {
  type CommandPaletteTarget,
  rankCommandPaletteTargets,
} from 'features/commandPalette/utils/commandPaletteTargets'
import { SelectedTab } from 'features/navbar/types'
import { getEntryTypeMetadata } from 'features/search/config/EntryTypeConfig'
import { useGlobalSearch } from 'features/search/hooks/useGlobalSearch'
import { getSearchResultUrl } from 'features/search/utils/getSearchResultUrl'
import compact from 'lodash/compact'
import { useMemo } from 'react'
import { type GeckoEntry, RolodexEntryType } from 'types'

import { useCommandPaletteDocumentTargets } from './useCommandPaletteDocumentTargets'
import { useCommandPaletteModelTargets } from './useCommandPaletteModelTargets'
import { useCommandPalettePageTargets } from './useCommandPalettePageTargets'
import { useCommandPaletteRecentTargets } from './useCommandPaletteRecentTargets'
import { useCommandPaletteSettingsTargets } from './useCommandPaletteSettingsTargets'
import { useHeldWhileSearching } from './useHeldWhileSearching'
import { useIsNavItemPermitted } from './useIsNavItemPermitted'

/**
 * Asset models are left out: no page shows one, so they only ever duplicated
 * the unit row.
 */
const ENTITY_DESTINATIONS = new Map<RolodexEntryType, SelectedTab>([
  [RolodexEntryType.UNIT, SelectedTab.Assets],
  [RolodexEntryType.INSPECTION, SelectedTab.Assets],
  [RolodexEntryType.KESTREL_MODEL, SelectedTab.Assets],
  [RolodexEntryType.SYSTEM, SelectedTab.SystemLens],
  [RolodexEntryType.JOB, SelectedTab.Inbox],
  [RolodexEntryType.VALUE_STORY, SelectedTab.ValueStories],
])

const ENTITY_RESULT_LIMIT = 30
const ENTITY_DEBOUNCE_MS = 150

const entryLabel = (entry: GeckoEntry): string => {
  const name = entry.data.name
  return typeof name === 'string' && name.trim() ? name : entry.id
}

function toEntityTarget(
  entry: GeckoEntry,
  siteId: string,
  router: AnyRouter
): CommandPaletteTarget | undefined {
  const destination = ENTITY_DESTINATIONS.get(entry.type)
  const navigation = getSearchResultUrl(entry, siteId)
  if (!destination || !navigation) return undefined
  const metadata = getEntryTypeMetadata(entry.type)
  return {
    id: `entity:${entry.type}:${entry.id}`,
    kind: 'entity',
    label: entryLabel(entry),
    kindLabel: metadata.pluralName,
    icon: metadata.icon,
    destination,
    href: router.buildLocation({
      to: navigation.to,
      params: navigation.params,
      search: navigation.search,
    }).href,
  }
}

export interface UseCommandPaletteTargetsResult {
  targets: CommandPaletteTarget[]
  isSearching: boolean
}

/**
 * Everything the palette can open for a site, ranked together. Settings pages
 * are reachable by typing only, never listed with no query.
 */
export function useCommandPaletteTargets({
  query,
  siteId,
  orgId,
  enabled,
}: {
  query: string
  siteId: string | undefined
  orgId: string | undefined
  enabled: boolean
}): UseCommandPaletteTargetsResult {
  const router = useRouter()
  const isNavItemPermitted = useIsNavItemPermitted({ siteId, orgId })
  const hasQuery = query.trim().length > 0

  const entityTypes = useMemo(
    () =>
      Array.from(ENTITY_DESTINATIONS.entries())
        .filter(([, destination]) => isNavItemPermitted(destination))
        .map(([type]) => type),
    [isNavItemPermitted]
  )

  const pageTargets = useCommandPalettePageTargets({ siteId, orgId })
  const recentTargets = useCommandPaletteRecentTargets({
    siteId,
    orgId,
    enabled,
  })
  const settingsTargets = useCommandPaletteSettingsTargets({ siteId, orgId })
  const modelTargets = useCommandPaletteModelTargets({
    siteId,
    orgId,
    enabled,
  })

  const { filteredResults, isLoading, isDebouncing } = useGlobalSearch(
    enabled && entityTypes.length > 0 ? query : '',
    siteId,
    orgId,
    {
      searchableTypes: entityTypes,
      expandedLimit: ENTITY_RESULT_LIMIT,
      debounceMs: ENTITY_DEBOUNCE_MS,
      minSearchLength: 1,
    }
  )
  const entityTargets = useMemo(() => {
    if (!siteId) return []
    return compact(
      filteredResults.map(({ entry }) => toEntityTarget(entry, siteId, router))
    )
  }, [filteredResults, router, siteId])
  const isSearchingEntities = enabled && hasQuery && (isDebouncing || isLoading)
  const heldEntityTargets = useHeldWhileSearching(
    entityTargets,
    isSearchingEntities,
    siteId
  )

  const { targets: documentTargets, isSearching: isSearchingDocuments } =
    useCommandPaletteDocumentTargets({
      query,
      siteId,
      enabled: enabled && isNavItemPermitted(SelectedTab.Documents),
    })
  const heldDocumentTargets = useHeldWhileSearching(
    documentTargets,
    isSearchingDocuments,
    siteId
  )

  const targets = useMemo(
    () =>
      rankCommandPaletteTargets(
        hasQuery
          ? [
              ...pageTargets,
              ...settingsTargets,
              ...heldEntityTargets,
              ...modelTargets,
              ...heldDocumentTargets,
            ]
          : [
              ...recentTargets,
              ...pagesNotListedAsRecent(pageTargets, recentTargets),
            ],
        query
      ),
    [
      hasQuery,
      recentTargets,
      heldDocumentTargets,
      heldEntityTargets,
      modelTargets,
      pageTargets,
      query,
      settingsTargets,
    ]
  )

  return {
    targets,
    isSearching: isSearchingEntities || isSearchingDocuments,
  }
}
